/**
 * Widget "แจ้งปัญหา / ขอฟีเจอร์" มุมขวาล่าง — เฉพาะ HR/Owner.
 * - แคปหน้าจอ (html-to-image, ได้ทุกอุปกรณ์) · อัดวิดีโอจอ (getDisplayMedia, เดสก์ท็อป) ·
 *   แนบไฟล์เอง (universal fallback)
 * - อัปโหลด media → R2 ผ่าน /upload (purpose=feedback) แล้ว POST /feedback
 * - เก็บ context อัตโนมัติ (หน้าไหน / browser / ขนาดจอ / role)
 */
import { useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import { MessageSquarePlus, Bug, Lightbulb, X, Camera, Video, Square, Paperclip, Send, Loader2, Trash2, CheckCircle2, FileText } from 'lucide-react';
import { useAuthStore } from '../lib/store';
import { useSubmitFeedback } from '../hooks';
import { uploadFile } from '../lib/api-client';
import { useToast } from './ui/toast';

interface Shot { blob: Blob; url: string; name: string; kind: 'image' | 'video' | 'file' }

export function FeedbackWidget() {
  const role = useAuthStore((s) => s.profile?.role);
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<'bug' | 'feature'>('bug');
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [shots, setShots] = useState<Shot[]>([]);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [recording, setRecording] = useState(false);
  const recRef = useRef<MediaRecorder | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const submit = useSubmitFeedback();
  const toast = useToast();

  // เฉพาะ HR/Owner
  if (role !== 'OWNER' && role !== 'HR') return null;

  const canRecord = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;

  function addShot(blob: Blob, k: 'image' | 'video' | 'file', name: string) {
    setShots((s) => (s.length >= 5 ? (toast.error('แนบได้สูงสุด 5 ไฟล์'), s) : [...s, { blob, url: URL.createObjectURL(blob), name, kind: k }]));
  }
  function removeShot(i: number) { setShots((s) => s.filter((_, idx) => idx !== i)); }

  async function takeScreenshot() {
    setOpen(false);
    await new Promise((r) => setTimeout(r, 300)); // รอ panel ปิดก่อนแคป
    try {
      const dataUrl = await toPng(document.body, { pixelRatio: 1, filter: (n) => !(n as HTMLElement)?.dataset?.feedbackWidget });
      const blob = await (await fetch(dataUrl)).blob();
      addShot(blob, 'image', `screenshot-${Date.now()}.png`);
    } catch {
      toast.error('แคปหน้าจออัตโนมัติไม่ได้ — ลองปุ่ม "แนบไฟล์" แทน');
    } finally {
      setOpen(true);
    }
  }

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 10 }, audio: false });
      const mr = new MediaRecorder(stream, { mimeType: 'video/webm', videoBitsPerSecond: 1_000_000 });
      const chunks: Blob[] = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunks, { type: 'video/webm' });
        if (blob.size > 25 * 1024 * 1024) { toast.error('คลิปใหญ่เกิน 25MB — อัดสั้นลงหน่อย'); return; }
        addShot(blob, 'video', `recording-${Date.now()}.webm`);
      };
      stream.getVideoTracks()[0]?.addEventListener('ended', () => { if (mr.state !== 'inactive') mr.stop(); });
      recRef.current = mr;
      mr.start();
      setRecording(true);
      setTimeout(() => { if (mr.state !== 'inactive') mr.stop(); }, 60_000); // cap 60 วิ
    } catch {
      toast.error('อัดวิดีโอไม่ได้ (รองรับเฉพาะเบราว์เซอร์เดสก์ท็อป)');
    }
  }

  function onPickFiles(e: React.ChangeEvent<HTMLInputElement>) {
    for (const f of Array.from(e.target.files ?? [])) {
      // เอกสาร/สเปรดชีต/PDF ฯลฯ ก็แนบได้ ไม่ใช่แค่รูป/วิดีโอ
      const k = f.type.startsWith('video') ? 'video' : f.type.startsWith('image') ? 'image' : 'file';
      addShot(f, k, f.name);
    }
    if (fileRef.current) fileRef.current.value = '';
  }

  async function send() {
    if (!title.trim()) { toast.error('กรอกหัวข้อก่อน'); return; }
    setBusy(true);
    try {
      const media: Array<{ path: string; type?: string; name?: string }> = [];
      for (const s of shots) {
        const path = await uploadFile(s.blob, 'feedback', s.name);
        media.push({ path, type: s.blob.type, name: s.name });
      }
      await submit.mutateAsync({
        kind,
        title: title.trim(),
        detail: detail.trim() || undefined,
        page_url: location.pathname + location.search,
        context: {
          user_agent: navigator.userAgent,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          app_version: (import.meta.env.VITE_APP_VERSION as string) ?? null,
        },
        media,
      });
      // คอนเฟิร์มที่มุมขวาล่าง (ตรงตำแหน่ง widget) ไม่เด้งกลางจอ
      setTitle(''); setDetail(''); setShots([]); setKind('bug'); setOpen(false);
      setSent(true);
      setTimeout(() => setSent(false), 3500);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ส่งไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-feedback-widget className="fixed bottom-4 right-4 z-[60] print:hidden flex flex-col items-end gap-2">
      {sent && (
        <div className="flex items-center gap-2 rounded-xl bg-green-600 text-white shadow-lg px-4 py-3 text-sm">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          ส่งแล้ว — ขอบคุณครับ ทีมงานได้รับเรียบร้อย
        </div>
      )}
      {!open && !sent && (
        <button
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 rounded-full bg-brand-600 text-white shadow-lg px-4 py-3 hover:bg-brand-700 transition"
          title="แจ้งปัญหา / ขอฟีเจอร์"
        >
          <MessageSquarePlus className="w-5 h-5" />
          <span className="text-sm font-medium hidden sm:inline">แจ้งปัญหา / ขอฟีเจอร์</span>
        </button>
      )}

      {open && (
        <div className="w-[min(92vw,380px)] rounded-2xl bg-white shadow-2xl border border-gray-200 p-4 max-h-[80vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">แจ้งปัญหา / ขอฟีเจอร์</h3>
            <button onClick={() => setOpen(false)}><X className="w-5 h-5 text-gray-400" /></button>
          </div>

          {/* ประเภท */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <button
              onClick={() => setKind('bug')}
              className={`flex items-center justify-center gap-1.5 rounded-lg border py-2 text-sm ${kind === 'bug' ? 'border-brand-600 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-600'}`}
            >
              <Bug className="w-4 h-4" /> แจ้งปัญหา
            </button>
            <button
              onClick={() => setKind('feature')}
              className={`flex items-center justify-center gap-1.5 rounded-lg border py-2 text-sm ${kind === 'feature' ? 'border-brand-600 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-600'}`}
            >
              <Lightbulb className="w-4 h-4" /> ขอฟีเจอร์
            </button>
          </div>

          <input
            className="input mb-2"
            placeholder={kind === 'bug' ? 'หัวข้อปัญหา เช่น กดอนุมัติแล้วค้าง' : 'อยากได้ฟีเจอร์อะไร'}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
          />
          <textarea
            className="input mb-2"
            rows={3}
            placeholder="อธิบายเพิ่มเติม (ทำอะไรอยู่ / คาดว่าจะเป็นยังไง)"
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            maxLength={5000}
          />

          {/* แนบหลักฐาน */}
          <div className="flex flex-wrap gap-2 mb-2">
            <button onClick={takeScreenshot} className="btn-secondary text-xs"><Camera className="w-3.5 h-3.5" /> แคปหน้าจอ</button>
            {canRecord && !recording && (
              <button onClick={startRecording} className="btn-secondary text-xs"><Video className="w-3.5 h-3.5" /> อัดวิดีโอ</button>
            )}
            {recording && (
              <button onClick={() => recRef.current?.stop()} className="btn-secondary text-xs text-red-600 border-red-300"><Square className="w-3.5 h-3.5" /> หยุดอัด</button>
            )}
            <button onClick={() => fileRef.current?.click()} className="btn-secondary text-xs"><Paperclip className="w-3.5 h-3.5" /> แนบไฟล์</button>
            {/* รับทั้งรูป/วิดีโอ และเอกสาร (PDF/Word/Excel/CSV) — เดิมล็อกไว้แค่ image/video
                ทำให้ลูกค้าเลือกไฟล์เอกสารไม่ได้ ได้แต่เปิดโฟลเดอร์วน */}
            <input
              ref={fileRef}
              type="file"
              accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx"
              multiple
              hidden
              onChange={onPickFiles}
            />
          </div>
          {recording && <p className="text-xs text-red-600 mb-2">● กำลังอัดวิดีโอจอ… (กด "หยุดอัด" เมื่อเสร็จ · สูงสุด 60 วิ)</p>}

          {/* ไฟล์ที่แนบ */}
          {shots.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {shots.map((s, i) => (
                <div key={i} className="relative">
                  {s.kind === 'image' ? (
                    <img src={s.url} alt="" className="w-16 h-16 object-cover rounded border" />
                  ) : s.kind === 'video' ? (
                    <video src={s.url} className="w-16 h-16 object-cover rounded border" />
                  ) : (
                    <div className="w-16 h-16 rounded border bg-gray-50 flex flex-col items-center justify-center p-1 gap-0.5" title={s.name}>
                      <FileText className="w-5 h-5 text-gray-400 shrink-0" />
                      <span className="text-[9px] text-gray-500 leading-tight text-center line-clamp-2 break-all">{s.name}</span>
                    </div>
                  )}
                  <button onClick={() => removeShot(i)} className="absolute -top-1.5 -right-1.5 bg-white rounded-full border shadow p-0.5">
                    <Trash2 className="w-3 h-3 text-red-500" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <button onClick={send} disabled={busy} className="btn-primary w-full text-sm">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> กำลังส่ง…</> : <><Send className="w-4 h-4" /> ส่งให้ทีมงาน</>}
          </button>
          <p className="text-[11px] text-gray-400 mt-2">ระบบจะแนบหน้าที่คุณอยู่ + ข้อมูลอุปกรณ์ให้อัตโนมัติ เพื่อให้ทีมงานแก้ได้ตรงจุด</p>
        </div>
      )}
    </div>
  );
}
